import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { saveAs } from 'file-saver';

import { ITransferBatchMassCreateResult } from 'src/app/models/generic';
import { formatSystemDate, parseSystemInstant } from 'src/app/shared/utils/system-date.util';

interface IMassCreateReportRow {
  Categoria: string;
  Lote: string;
  QueryNumber: string;
  Contrato: string;
  Versao: string;
  Parceiro: string;
  Cnpj: string;
  Motivo: string;
  IsNote: boolean;
}

// RN01: contrato que não entra em lote é falha da rodada - o motivo é exibido como "Falha: ...".
const EXCLUDED_CATEGORY_LABEL: Record<string, string> = {
  AliquotaZerada: 'Falha: Alíquota não encontrada ou igual a zero',
  CadastroParceiroIncompleto: 'Falha: Cadastro do parceiro não concluído'
};

const STATUS_LABEL: Record<string, string> = {
  CreatedNew: 'Lote criado',
  AddedToExisting: 'Lote reaproveitado',
  SkippedEmpty: 'Vazio',
  Failed: 'Falha'
};

// Andamento por contrato do job em background (parceiro em processamento agora).
export interface IMassCreateProgress {
  TotalContracts: number;
  ProcessedContracts: number;
  CurrentBeneficiaryName?: string;
  CurrentContractCount: number;
}

// Tempo estimado por contrato enquanto ainda não há histórico da própria execução.
const DEFAULT_SECONDS_PER_CONTRACT = 0.5;
// A barra estimada anda até esta fração da fatia do parceiro atual e espera o valor real.
const MAX_SLICE_FRACTION = 0.95;

export type MassCreateModalState = 'loading' | 'running' | 'result' | 'error';

// Identificação de qual execução em background esse relatório pertence -
// ajuda quem está olhando a saber exatamente qual rodada é aquela,
// principalmente ao reabrir via "Ver última execução".
export interface IMassCreateExecutionInfo {
  ExecutionId?: string;
  StartedAt?: string;
  FinishedAt?: string;
  TriggeredByUsername?: string;
}

@Component({
  selector: 'app-modal-transfer-batch-mass-create-result',
  templateUrl: './modal-transfer-batch-mass-create-result.component.html',
  styleUrls: ['./modal-transfer-batch-mass-create-result.component.scss']
})
export class ModalTransferBatchMassCreateResultComponent implements OnInit, OnDestroy {

  public result: ITransferBatchMassCreateResult | null = null;
  public rows: IMassCreateReportRow[] = [];
  public state: MassCreateModalState = 'loading';
  public processedCount = 0;
  public totalCount = 0;
  public errorMessage = '';
  public executionInfo: IMassCreateExecutionInfo | null = null;
  public progress: IMassCreateProgress | null = null;
  public now = Date.now();

  private partnerKey = '';
  private partnerStartedAt = Date.now();
  private shownPercent = 0;
  private ticker?: ReturnType<typeof setInterval>;

  // Os totais saem das listas do relatório (e não dos contadores persistidos na execução) para
  // bater sempre com o que está listado - inclusive em execuções antigas, gravadas quando
  // "Falhas" só contava parceiros que deram erro.

  // Contratos processados na rodada: os dos parceiros processados + os que ficaram de fora.
  get processedContractsCount(): number {
    const inGroups = (this.result?.Items || []).reduce((sum, item) => sum + this.contractsOf(item), 0);

    return inGroups + (this.result?.ExcludedContracts?.length || 0);
  }

  // RN01: cada contrato não loteado, e cada contrato de um parceiro que falhou, conta como falha.
  get failureCount(): number {
    const inFailedGroups = (this.result?.Items || [])
      .filter((item) => item.Status === 'Failed')
      .reduce((sum, item) => sum + this.contractsOf(item), 0);

    return inFailedGroups + (this.result?.ExcludedContracts?.length || 0);
  }

  private contractsOf(item: { Contracts?: unknown[]; ContractCount?: number }): number {
    return item.Contracts?.length || item.ContractCount || 0;
  }

  get progressPercent(): number {
    if (!this.totalCount) {
      return 0;
    }
    return Math.min(100, Math.round((this.processedCount / this.totalCount) * 100));
  }

  constructor(
    public _dialogRef: MatDialogRef<ModalTransferBatchMassCreateResultComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { Result: ITransferBatchMassCreateResult | null }
  ) { }

  ngOnInit(): void {
    if (this.data?.Result) {
      this.setResult(this.data.Result);
    }

    // Relógio da tela: faz a barra estimada e o tempo decorrido andarem entre uma consulta e outra (a cada 3 s).
    this.ticker = setInterval(() => (this.now = Date.now()), 500);
  }

  ngOnDestroy(): void {
    if (this.ticker) {
      clearInterval(this.ticker);
    }
  }

  // Job em background (Hangfire) informando quantos parceiros já foram
  // processados - chamado a cada rodada do polling enquanto a execução
  // ainda está Queued/Running.
  setRunning(
    processedCount: number,
    totalCount: number,
    executionInfo?: IMassCreateExecutionInfo,
    progress?: IMassCreateProgress
  ): void {
    this.state = 'running';
    this.processedCount = processedCount;
    this.totalCount = totalCount;
    if (executionInfo) {
      this.executionInfo = executionInfo;
    }

    if (progress) {
      this.progress = progress;
      this.trackPartnerStart();
    }
  }

  // --- Progresso: valor real + estimativa dentro do parceiro atual -----------------------------------
  // Parceiro com muitos contratos demora e o valor real só muda quando ele termina - a tela ficaria parada
  // em 0%. Então a barra anda de forma estimada (nunca passa de 95% da fatia do parceiro atual nem de
  // 99% no total) e salta para o valor real quando o parceiro termina.

  get hasContractProgress(): boolean {
    return (this.progress?.TotalContracts ?? 0) > 0;
  }

  get currentPartnerText(): string | null {
    const name = this.progress?.CurrentBeneficiaryName;
    const contracts = this.progress?.CurrentContractCount ?? 0;

    if (!name) {
      return null;
    }

    const label = `${contracts} contrato${contracts === 1 ? '' : 's'}`;

    return `Processando ${name} (${label}) · parceiro ${this.processedCount + 1} de ${this.totalCount}`;
  }

  get contractsText(): string | null {
    return this.hasContractProgress
      ? `${this.progress!.ProcessedContracts} de ${this.progress!.TotalContracts} contratos concluídos`
      : null;
  }

  get elapsedText(): string | null {
    const start = this.executionStartMs();

    if (!start) {
      return null;
    }

    const totalSeconds = Math.max(0, Math.floor((this.now - start) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return minutes > 0 ? `${minutes} min ${seconds} s` : `${seconds} s`;
  }

  get displayPercent(): number {
    // nunca volta para trás
    this.shownPercent = Math.max(this.shownPercent, Math.floor(this.estimatedPercent));

    return this.shownPercent;
  }

  private get realPercent(): number {
    if (this.hasContractProgress) {
      return (this.progress!.ProcessedContracts / this.progress!.TotalContracts) * 100;
    }

    return this.totalCount ? (this.processedCount / this.totalCount) * 100 : 0;
  }

  private get estimatedPercent(): number {
    const current = this.progress?.CurrentContractCount ?? 0;

    if (!this.hasContractProgress || !current) {
      return this.realPercent;
    }

    const slice = (current / this.progress!.TotalContracts) * 100;
    const expectedSeconds = Math.max(current * this.secondsPerContract(), 5);
    const elapsedSeconds = Math.max(0, (this.now - this.partnerStartedAt) / 1000);
    // curva suave: sobe rápido no começo e desacelera, sem nunca chegar ao fim da fatia
    const fraction = Math.min(MAX_SLICE_FRACTION, 1 - Math.exp(-elapsedSeconds / expectedSeconds));

    return Math.min(99, this.realPercent + slice * fraction);
  }

  private secondsPerContract(): number {
    const done = this.progress?.ProcessedContracts ?? 0;
    const start = this.executionStartMs();

    if (!done || !start) {
      return DEFAULT_SECONDS_PER_CONTRACT;
    }

    const secondsInCurrentPartner = (this.now - this.partnerStartedAt) / 1000;
    const secondsOnFinished = (this.now - start) / 1000 - secondsInCurrentPartner;

    return secondsOnFinished > 0 ? secondsOnFinished / done : DEFAULT_SECONDS_PER_CONTRACT;
  }

  // Marca o início do parceiro atual quando a consulta mostra que mudou (ou que é o primeiro).
  private trackPartnerStart(): void {
    const key = `${this.progress?.ProcessedContracts}|${this.progress?.CurrentBeneficiaryName}`;

    if (key === this.partnerKey) {
      return;
    }

    const isFirstObservation = this.partnerKey === '';
    this.partnerKey = key;

    const start = this.executionStartMs();
    this.partnerStartedAt =
      isFirstObservation && !this.progress?.ProcessedContracts && start ? start : Date.now();
  }

  private executionStartMs(): number | null {
    const started = this.executionInfo?.StartedAt;

    if (!started) {
      return null;
    }

    const ms = parseSystemInstant(started).getTime();

    return Number.isFinite(ms) ? ms : null;
  }

  // Chamado de fora (via dialogRef.componentInstance) quando o polling
  // detecta que a execução terminou (Status Completed) - o dialog nasce em
  // estado de carregamento (skeleton) e só recebe o resultado quando o job
  // em background termina.
  setResult(result: ITransferBatchMassCreateResult, executionInfo?: IMassCreateExecutionInfo): void {
    this.result = result;
    this.rows = this.buildRows(result);
    this.state = 'result';
    if (executionInfo) {
      this.executionInfo = executionInfo;
    }
  }

  // Chamado quando a execução falhou (Status Failed) ou o polling não
  // conseguiu mais consultar o andamento.
  setError(message?: string, executionInfo?: IMassCreateExecutionInfo): void {
    this.errorMessage = message || 'Falha ao gerar lotes automáticos.';
    this.state = 'error';
    if (executionInfo) {
      this.executionInfo = executionInfo;
    }
  }

  private buildRows(result: ITransferBatchMassCreateResult): IMassCreateReportRow[] {
    const rows: IMassCreateReportRow[] = [];

    (result.Items || []).forEach(item => {
      const categoria = STATUS_LABEL[item.Status] || item.Status;

      if (item.Contracts && item.Contracts.length > 0) {
        item.Contracts.forEach(contract => {
          rows.push({
            Categoria: categoria,
            Lote: item.BatchNumber || '-',
            QueryNumber: contract.QueryNumber || '-',
            Contrato: contract.Number || '-',
            Versao: contract.Version != null ? String(contract.Version) : '-',
            Parceiro: contract.BeneficiaryName || item.BeneficiaryName || '-',
            Cnpj: contract.BeneficiaryDocument || item.BeneficiaryDocument || '-',
            Motivo: item.Message || '-',
            IsNote: false
          });
        });
      } else {
        rows.push({
          Categoria: categoria,
          Lote: item.BatchNumber || '-',
          QueryNumber: '-',
          Contrato: '-',
          Versao: '-',
          Parceiro: item.BeneficiaryName || '-',
          Cnpj: item.BeneficiaryDocument || '-',
          Motivo: item.Message || '-',
          IsNote: false
        });
      }

      (item.Notes || []).forEach(note => {
        rows.push({
          Categoria: 'Informativo',
          Lote: note.BatchNumber || '-',
          QueryNumber: '-',
          Contrato: '-',
          Versao: '-',
          Parceiro: item.BeneficiaryName || '-',
          Cnpj: item.BeneficiaryDocument || '-',
          Motivo: note.Reason,
          IsNote: true
        });
      });
    });

    (result.ExcludedContracts || []).forEach(excluded => {
      rows.push({
        Categoria: 'Não loteado',
        Lote: '-',
        QueryNumber: excluded.QueryNumber || '-',
        Contrato: excluded.Number || '-',
        Versao: excluded.Version != null ? String(excluded.Version) : '-',
        Parceiro: excluded.BeneficiaryName || '-',
        Cnpj: excluded.BeneficiaryDocument || '-',
        Motivo: EXCLUDED_CATEGORY_LABEL[excluded.Category] || excluded.Category || '-',
        IsNote: false
      });
    });

    return rows;
  }

  exportCsv(): void {
    const header = ['Categoria', 'Lote', 'Query Number', 'Contrato', 'Versão', 'Parceiro', 'CNPJ', 'Motivo'];
    let content = header.join(';') + '\r\n';

    this.rows.forEach(row => {
      content += [
        this.csvEscape(row.Categoria),
        this.csvEscape(row.Lote),
        this.csvEscape(row.QueryNumber),
        this.csvEscape(row.Contrato),
        this.csvEscape(row.Versao),
        this.csvEscape(row.Parceiro),
        this.csvEscape(row.Cnpj),
        this.csvEscape(row.Motivo)
      ].join(';') + '\r\n';
    });

    const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8;' });
    // Horário de Brasília (e não UTC) para o nome do arquivo bater com o horário em que o usuário executou.
    const timestamp = formatSystemDate(new Date(), "yyyy-MM-dd'T'HH-mm-ss");
    saveAs(blob, `relatorio-loteamento-automatico-${timestamp}.csv`);
  }

  private csvEscape(value: string): string {
    if (value == null) {
      return '';
    }
    if (value.includes(';') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }

  close(): void {
    this._dialogRef.close(null);
  }
}
